import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/common/Breadcrumb";
import ArticleCard from "@/components/common/ArticleCard";
import { articles } from "@/mocks/articles";
import { formatDateLabel, formatViews } from "@/utils/format";
import { SITE_ORIGIN } from "@/config/site";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-primary-500">
          <i className="ri-emotion-sad-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇瓜料不见了</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          它可能已经被更新或合并到其他稿件里。回到列表页，还有更多值得一看的内容在等你。
        </p>
        <Link
          to="/news"
          className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回全部瓜料
        </Link>
      </div>
    );
  }

  const related = articles.filter((item) => item.category === article.category && item.id !== article.id).slice(0, 3);
  const fallbackRelated = articles.filter((item) => item.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "诱惑吃瓜" },
      mainEntityOfPage: `${SITE_ORIGIN}/article/${article.id}`,
      keywords: article.tags.join(","),
      articleSection: article.categoryName,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "全部瓜料", item: `${SITE_ORIGIN}/news` },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `${SITE_ORIGIN}/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} | 诱惑吃瓜`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        path={`/article/${article.id}`}
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-6 md:py-10">
        <div className="max-w-4xl">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "全部瓜料", to: "/news" },
              { label: article.categoryName, to: `/news?cat=${article.category}` },
              { label: article.title },
            ]}
          />
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <Link
              to={`/news?cat=${article.category}`}
              className="rounded-full bg-primary-500 px-3 py-1 font-medium text-background-50"
            >
              {article.categoryName}
            </Link>
            {article.hot && (
              <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                <i className="ri-fire-line" aria-hidden="true" />
                高能热帖
              </span>
            )}
          </div>
          <h1 className="mt-4 text-balance font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line" aria-hidden="true" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true" />
              {formatDateLabel(article.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(article.views)} 次阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {article.comments} 条讨论
            </span>
          </div>
        </div>
      </div>

      <div className="px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-4">
          <article className="lg:col-span-3">
            <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
              <img
                src={article.cover}
                alt={`${article.title} - ${article.categoryName}配图`}
                title={`${article.title} - 诱惑吃瓜`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5 text-[15px] leading-8 text-foreground-700">
              {article.content.map((paragraph, index) => (
                <p key={`${article.id}-p-${index}`}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-sm text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/news?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </article>

          <aside className="space-y-6 lg:col-span-1">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
              <ul className="mt-4 space-y-4">
                {relatedList.map((item) => (
                  <li key={item.id}>
                    <Link to={`/article/${item.id}`} className="group flex gap-3">
                      <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} - 相关推荐配图`}
                          title={`${item.title} - 诱惑吃瓜`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {item.categoryName}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-accent-300 bg-accent-100/70 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">继续吃瓜</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                这篇看完了？列表页还有更多按栏目整理好的内容，挑个你感兴趣的栏目接着看。
              </p>
              <Link
                to="/news"
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
              >
                前往全部瓜料
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </section>
          </aside>
        </div>
      </div>

      <section aria-labelledby="related-title" className="bg-background-100 px-4 py-12 md:px-6 md:py-14">
        <div className="mb-6 flex items-end justify-between gap-3">
          <h2 id="related-title" className="font-heading text-2xl font-bold text-foreground-950">
            同类推荐
          </h2>
          <Link
            to="/news"
            className="whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
          >
            更多内容
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {relatedList.map((item) => (
            <ArticleCard key={`bottom-${item.id}`} article={item} />
          ))}
        </div>
      </section>
    </>
  );
}